import _ from 'underscore';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Activity } from 'botframework-directlinejs';
import { isSystemMessage } from '@microsoft/omnichannel-chat-sdk';
import {
  ILogger,
  LiveAgentState,
  LiveAgentQueue,
} from '@bingads-webui-react/msa-copilot-common';
import {
  LiveAgentChatConfig,
  LiveAgentConnectionState,
  createStructuredHistory,
  getLiveAgentDirectLineMessage,
  getLiveAgentFormattedMessage,
  isCopilotChatHistoryMessage,
} from '../helpers';

import { useLiveAgentCookies } from './use-live-agent-cookies';
import { GetAuthTokenForQueueFunction, useLiveAgentChatSDKReducer } from './use-live-agent-chat-sdk-reducer';

interface ILiveAgentTransferProps {
  username: string,
  conversationHistory: Activity[],
  liveAgentChatConfig: LiveAgentChatConfig,
  locale: string,
  logger: ILogger,
  shouldResumeConversation: MutableRefObject<boolean>,
  copilotConversationId?: string,
  createLiveAgentAuthTokenGetter: GetAuthTokenForQueueFunction,
  isLiveAgentOnlyMode?: boolean,
  isAgentOutOfSupportEnabled?: boolean,
  customContext?: Record<string, { value: any; isDisplayable?: boolean }>,
  onMessageReceived?: (message: string) => void,
}

interface LiveAgentOptionalParams {
  reconnectId?: string;
  customContext?: Record<string, { value: any; isDisplayable?: boolean }>;
}

const LIVE_AGENT_TYPING_TIMEOUT = 2000;

export const useLiveAgentTransfer = (props: ILiveAgentTransferProps) => {
  const {
    username,
    conversationHistory,
    isLiveAgentOnlyMode,
    liveAgentChatConfig,
    locale,
    logger,
    shouldResumeConversation,
    copilotConversationId,
    createLiveAgentAuthTokenGetter,
    isAgentOutOfSupportEnabled = false,
    customContext,
    onMessageReceived,
  } = props;
  const conversationId = useRef<string | undefined>();
  const [isLiveAgentTyping, setIsLiveAgentTyping] = useState<boolean>(false);
  const [, setProblemDescription] = useState<string | undefined>();

  const [state, dispatch] = useLiveAgentChatSDKReducer(locale, createLiveAgentAuthTokenGetter, isLiveAgentOnlyMode, logger);
  const {
    liveAgentState,
    liveAgentStream,
    chatSDK,
  } = state;

  const {
    getLiveChatContext,
    setLiveChatContext,
    clearLiveChatCookies,
  } = useLiveAgentCookies(logger);

  const closeLiveAgentChat = useCallback((shouldShowSurvey: boolean = false) => {
    if (liveAgentStream && !liveAgentStream.isStopped && !liveAgentStream.closed) {
      logger?.trace('Copilot - Live Agent: Agent conversation ended');
      liveAgentStream.next(getLiveAgentDirectLineMessage({
        text: LiveAgentState.AGENT_END_CONVERSATION,
        channelId: 'directline',
        value: 'LiveAgentMessage',
        conversationId: conversationId.current || '',
      }));

      if (shouldShowSurvey) {
        liveAgentStream.next({
          ...getLiveAgentDirectLineMessage({
            text: LiveAgentState.AGENT_POST_CHAT_SURVEY,
            channelId: 'directline',
            value: 'LiveAgentMessage',
            conversationId: conversationId.current || '',
          }),
          firstName: liveAgentChatConfig?.surveyContext?.firstName || username,
          lastName: liveAgentChatConfig?.surveyContext?.lastName || '',
          localeId: liveAgentChatConfig?.surveyContext?.localeId || 'en-US',
          localeName: liveAgentChatConfig?.surveyContext?.localeName || 'English (United States)',
        });
      }

      clearLiveChatCookies();

      conversationId.current = undefined;
      dispatch({ type: 'disconnect', isLiveAgentOnlyMode, logger });
    }
  }, [logger, liveAgentStream, dispatch]);

  const debouncedSetIsLiveAgentTypingFalse = useMemo(() => _.debounce(() => setIsLiveAgentTyping(false), LIVE_AGENT_TYPING_TIMEOUT), []);

  useEffect(() => {
    const triggerLiveAgentChat = async () => {
      try {
        if (liveAgentStream && chatSDK && liveAgentState === LiveAgentConnectionState.DISCONNECTED) {
          logger?.trace('Copilot - Live Agent: liveAgentStream and chatSDK have been initialized, starting live agent chat...');
          dispatch({ type: 'in-progress' });

          const optionalParams: LiveAgentOptionalParams = {};

          const { chatReconnectContext } = getLiveChatContext();
          if (chatReconnectContext?.reconnectId) {
            logger?.trace('Copilot - Live Agent: Restoring existing conversation');
            optionalParams.reconnectId = chatReconnectContext.reconnectId;
          } else {
            logger?.trace('Copilot - Live Agent: Starting new conversation');
          }

          if (customContext) {
            optionalParams.customContext = customContext;
          }

          // Deep cloning optional params as it is getting mutated by chatSDK
          const clonedOptionalParams = structuredClone(optionalParams);

          await chatSDK.initialize();

          // Checking if live chat is available
          if (isAgentOutOfSupportEnabled) {
            const liveChatConfig = await chatSDK.getLiveChatConfig();
            const {
              LiveWSAndLiveChatEngJoin: {
                OutOfOperatingHours,
              },
            } = liveChatConfig;
            if (OutOfOperatingHours === 'True') {
              logger?.trace(`Copilot - Live Agent: Out of operating hours, chat not available. Locale: ${locale} [${new Date().toISOString()}]`);
              closeLiveAgentChat();
              return;
            }
          }

          await chatSDK.startChat(clonedOptionalParams);

          if (chatReconnectContext?.reconnectId) {
            dispatch({ type: 'connected' });
            logger?.trace(`Copilot - Live Agent: Restore conversation complete, reconnectId=${chatReconnectContext.reconnectId}`);
          }

          if (!conversationId.current) {
            const conversationDetails = await chatSDK.getConversationDetails();
            conversationId.current = conversationDetails?.conversationId;
            logger?.trace(`Copilot - Live Agent: Conversation details fetched for conversationId - ${conversationId.current}`);
          }

          const messages = await chatSDK.getMessages();
          if (messages) {
            for (let i = messages.length - 1; i >= 0; i -= 1) {
              const message = messages[i];

              // populate stream with previous messages
              const isHiddenFromUser = isCopilotChatHistoryMessage(message.content);
              const formattedMessage = {
                ...getLiveAgentFormattedMessage(username, message, conversationId.current || '', isHiddenFromUser),
                isFromRestore: true,
              };
              liveAgentStream.next(formattedMessage);
            }
          }

          // Sending messages to live agent
          liveAgentStream.subscribe(async (event) => {
            // We ignore directline messages as they don't need to be sent to live agent
            // We are sending it as part of conversation history event
            if (event.type === 'message' && event.channelId !== 'directline') {
              logger?.trace(`Copilot - Live Agent: User question - ${event.text}`);
              await chatSDK.sendMessage({
                content: event.text,
              });
            }
          });

          // Agent typing state
          chatSDK.onTypingEvent(() => {
            setIsLiveAgentTyping(true);
            debouncedSetIsLiveAgentTypingFalse();
          });

          // Listening to new messages from live agent
          chatSDK.onNewMessage(async (message: any) => {
            onMessageReceived?.(message);
            if (message.tags && message.tags.includes(LiveAgentState.AGENT_ACCEPTED)) {
              dispatch({ type: 'connected' });
              logger?.trace(`Copilot - Live Agent: Agent conversation accepted, conversationId=${conversationId.current}`);

              // Setting the chat context in cookie for resuming the conversation/refresh scenario
              const reconnectContext = await chatSDK.getChatReconnectContext();
              if (reconnectContext && copilotConversationId) {
                setLiveChatContext({
                  chatReconnectContext: reconnectContext,
                  copilotConversationId,
                });
              }
            } else if (message.tags && message.tags.includes(LiveAgentState.AGENT_END_CONVERSATION)) {
              shouldResumeConversation.current = true;
              closeLiveAgentChat(true);
              return;
            }

            if (isSystemMessage(message)) {
              logger?.trace(`Copilot - Live Agent: System message - ${message.content}`);
            }

            if (message.content?.includes('SYSTEM_TRIGGER_FOR_SURVEY')) {
              logger?.trace(`Copilot - Live Agent: Ignoring Survey - ${message.content}`);
              return;
            }

            const agentMessage = getLiveAgentFormattedMessage(username, message, conversationId.current || '');
            liveAgentStream.next(agentMessage);
          });

          if (!chatReconnectContext) {
            // send conversation history between user and bot to live agent
            if (conversationHistory && conversationHistory.length) {
              const messageHistory = createStructuredHistory(conversationHistory, username, 20 * 1000);
              logger?.trace(`Copilot -  Live Agent: Sending conversation history: ${messageHistory}`);

              liveAgentStream.next(getLiveAgentDirectLineMessage({
                fromName: username,
                text: messageHistory,
                value: { isHiddenFromUser: true },
              }));
            }

            setProblemDescription((prev) => {
              if (prev) {
                logger?.trace(`Copilot -  Live Agent: Sending problem description: ${prev}`);
                liveAgentStream.next(getLiveAgentDirectLineMessage({
                  fromName: username,
                  text: `User-Copilot Conversation History: ${prev}`,
                  value: { isHiddenFromUser: true },
                }));
              }
              return undefined;
            });
          }
        }
      } catch (error: any) {
        logger?.error(`Copilot - Live Agent: Error - ${error}`);
        closeLiveAgentChat();
      }
    };

    triggerLiveAgentChat();
  }, [
    chatSDK,
    closeLiveAgentChat,
    debouncedSetIsLiveAgentTypingFalse,
    dispatch,
    liveAgentState,
    liveAgentStream,
    logger,
    onMessageReceived,
  ]);

  const debouncedSendUserTypingEvent = useMemo(() => {
    if (!chatSDK) {
      return undefined;
    }
    // debounce the sendTypingEvent to avoid being throttled by OmniChannelChat SDK
    return _.debounce(() => chatSDK.sendTypingEvent(), 200, true);
  }, [chatSDK]);

  const sendLiveAgentTypingAgent = useCallback(() => {
    if (liveAgentState === LiveAgentConnectionState.CONNECTED) {
      debouncedSendUserTypingEvent?.();
    }
  }, [debouncedSendUserTypingEvent, liveAgentState]);

  const startLiveAgentChat = useCallback((queue?: LiveAgentQueue, problemDescription?: string) => {
    logger?.trace(`Copilot - Live Agent: Initializing live agent chat. queue=${queue}, problemDescription=${problemDescription}`);
    setProblemDescription(problemDescription);
    dispatch({ type: 'initialize', createLiveAgentAuthTokenGetter, queue });
  }, [createLiveAgentAuthTokenGetter, locale, logger]);

  return {
    liveAgentStream,
    liveAgentState,
    isLiveAgentTyping,
    startLiveAgentChat,
    closeLiveAgentChat,
    sendLiveAgentTypingAgent,
  };
};
